import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    // OUTGLOW fix O03b (22/10/2026): uma queda de rede (ou Wi-Fi sem internet, em que o navegador continua "online")
    // deixava fotos da galeria e as miniaturas quebradas para sempre: o navegador não pede de novo uma <img> que
    // falhou. Agora: novas tentativas com espera crescente depois de cada falha; pedido de novo ao subir o componente
    // (fotos que falharam antes do JS carregar), ao tocar na galeria, ao voltar a rede e ao voltar à aba. Os toques têm
    // intervalo crescente (5, 10, 20 e depois 30 s), para não virar enxurrada de pedidos num Wi-Fi sem internet.
    this.addEventListener('error', this.#handleImageError, { signal, capture: true });
    this.addEventListener('pointerdown', this.#handleGalleryPointerDown, { signal, passive: true });
    window.addEventListener('online', this.#handleOnline, { signal });
    document.addEventListener('visibilitychange', this.#handleVisibilityChange, { signal });
    this.#retryBrokenImages(false);
  }

  /** OUTGLOW fix O03b (22/10/2026): esperas (ms) das novas tentativas automáticas de uma foto que falhou. */
  static #RETRY_DELAYS = [2000, 5000, 10000, 20000];

  /** @type {WeakMap<HTMLImageElement, ReturnType<typeof setTimeout>>} */
  static #timers = new WeakMap();

  /** Intervalo mínimo (ms) até o próximo toque pedir de novo as fotos quebradas; dobra a cada uso (teto 30 s). */
  #touchRetryGap = 5000;

  #lastTouchRetry = 0;

  /** @param {Event} event */
  #handleImageError = (event) => {
    const img = event.target;
    if (!(img instanceof HTMLImageElement)) return;
    // Sem rede, o evento 'online' pede de novo. Com rede, até 4 novas tentativas automáticas (uma por vez);
    // depois disso, ao tocar na galeria, voltar a rede ou voltar à aba.
    if (!navigator.onLine || MediaGallery.#timers.has(img)) return;
    const attempt = Number(img.dataset.outglowRetries || 0);
    const delay = MediaGallery.#RETRY_DELAYS[attempt];
    if (delay === undefined) return;
    img.dataset.outglowRetries = String(attempt + 1);
    MediaGallery.#timers.set(
      img,
      setTimeout(() => {
        MediaGallery.#timers.delete(img);
        if (img.isConnected && MediaGallery.#isBroken(img)) MediaGallery.#reloadImage(img);
      }, delay)
    );
  };

  #handleGalleryPointerDown = () => {
    const now = Date.now();
    if (now - this.#lastTouchRetry < this.#touchRetryGap) return;
    if (!this.#retryBrokenImages(false)) return;
    this.#lastTouchRetry = now;
    this.#touchRetryGap = Math.min(this.#touchRetryGap * 2, 30000);
  };

  #handleOnline = () => this.#retryBrokenImages(true);

  #handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') this.#retryBrokenImages(true);
  };

  /**
   * Pede de novo as fotos quebradas da galeria (slides, miniaturas e zoom já aberto).
   * @param {boolean} resetCycle - rede ou aba de volta: zera as tentativas e o intervalo dos toques, e antecipa as
   *   já agendadas. Num toque (ou ao subir), só pede as que não têm nova tentativa agendada.
   * @returns {boolean} se alguma foto foi pedida de novo.
   */
  #retryBrokenImages(resetCycle) {
    if (!navigator.onLine) return false;
    if (resetCycle) this.#touchRetryGap = 5000;
    let retried = false;
    this.querySelectorAll('img').forEach((img) => {
      if (!MediaGallery.#isBroken(img)) return;
      const timer = MediaGallery.#timers.get(img);
      if (timer !== undefined) {
        if (!resetCycle) return;
        clearTimeout(timer);
        MediaGallery.#timers.delete(img);
      }
      if (resetCycle) delete img.dataset.outglowRetries;
      MediaGallery.#reloadImage(img);
      retried = true;
    });
    return retried;
  }

  /** @param {HTMLImageElement} img */
  static #isBroken(img) {
    return img.complete && img.naturalWidth === 0 && Boolean(img.currentSrc || img.src);
  }

  /** @param {HTMLImageElement} img */
  static #reloadImage(img) {
    const { srcset, src } = img;
    if (srcset) {
      img.srcset = '';
      img.srcset = srcset;
    }
    if (src) {
      img.removeAttribute('src');
      img.src = src;
    }
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
